import React, { useState , useEffect } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import "../styles/ServiceAmount.css";
import TermsConditionsPopup from "./TermsConditionsPopup"; // Import Terms & Conditions Popup
import Swal from "sweetalert2";
import RedisInfo from "../services/RedisInfo";
// CRITICAL FIX: Rate limiting removed for purchase flows - no longer importing rate limit utilities
// CRITICAL FIX: Add fallback for API URL if not set during build
const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

// ─────────────────────────────────────────────────────────────────────────────
// MAINTENANCE FLAG — set to true to pause all order purchases and show the
// "Under Maintenance" screen. Set back to false to re-enable purchases.
// ─────────────────────────────────────────────────────────────────────────────
const MAINTENANCE_MODE = false;

const PurchasePopup = ({ onClose, selectedPlan, serviceId, serviceName, autoDebitStatus }) => {
  
  const { t } = useTranslation();
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const planCost = selectedPlan ? selectedPlan.planCost : 0;
  const gstPercentage = 18;
  const gstAmount = (planCost * gstPercentage) / 100;
  const totalAmount = Number(planCost) + Number(gstAmount);
  const currentDate = new Date().toLocaleDateString("en-GB");

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        // CRITICAL FIX: Purchase flow bypasses rate limiting
        // Rate limiting removed for purchase/service order flows as per requirement
        const redisData = await RedisInfo.redisDetails();
        // Use EXACT same parameters as Home/Balance component so FundCheck result matches
        const forCode = "00";
        const outletCode = "00";
        const dealerCode =
          redisData.MultiServiceDealerCode ||
          sessionStorage.getItem("multiServiceDealerCode") ||
          localStorage.getItem("multiServiceDealerCode") ||
          "";
        const response = await fetch(`${apiUrl}/api/v1/FundCheck`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "X-Purchase-Context": "true" // Header to indicate this is a purchase-related call
          },
          body: JSON.stringify({
            CustCategory: "MASS",
            DealerCode: "MASS" + dealerCode,
            ForCode: forCode,
            OutletCode: outletCode,
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            throw new Error("Failed to fetch balance");
          }

          // CRITICAL FIX: Purchase flow should not receive 429 errors
          // If we get 429, it means backend didn't recognize purchase context
          // Log warning but don't show rate limit popup to user
          if (response.status === 429 && errorData.ErrorMsg) {
            console.warn("Rate limit error received during purchase flow - this should not happen:", errorData.ErrorMsg);
            setAvailableBalance(0);
            setBalanceLoaded(true);
            return;
          }

          throw new Error("Failed to fetch balance");
        }

        const data = await response.json();
        
        // CRITICAL FIX: Check for backend errors even when HTTP status is 200 OK
        // Backend returns 200 OK with ErrorCode: -1 for errors (like DNS resolution errors)
        if (data.ErrorCode === -1 || data.Status === "Error") {
          console.error("❌ Backend returned error during purchase flow:", data.ErrorMsg);
          if (data.ErrorMsg && data.ErrorMsg.includes("DNS Resolution Error")) {
            console.error("⚠️ DNS Resolution Error - SAP API hostname cannot be resolved from production server");
          }
          setAvailableBalance(0);
          setBalanceLoaded(true);
          return;
        }

        if (data.Data && data.Data.length > 0) {
          const balanceData = data.Data[0];
          // Parse as number so comparison with minRequiredBalance is correct (API may return string)
          const amount = Number(balanceData.AMOUNT) || Number(balanceData.X_AMOUNT) || 0;
          console.log("PurchasePopup FundCheck balance:", amount, "raw data:", balanceData);
          setAvailableBalance(amount);
        } else {
          setAvailableBalance(0);
        }
        setBalanceLoaded(true);
      } catch (error) {
        console.error("Error fetching balance:", error);
        setAvailableBalance(0);
        setBalanceLoaded(true);
      }
    };

    fetchBalance();
  }, []);

  // ── Maintenance gate ────────────────────────────────────────────────────────
  // When MAINTENANCE_MODE is true, render a maintenance screen and skip the
  // normal purchase flow entirely. Remove / set false to re-enable purchases.
  if (MAINTENANCE_MODE) {
    return (
      <div className="popup-overlay">
        <div className="popup-content" style={{ textAlign: "center", padding: "40px 32px" }}>
          <div className="popup-header" style={{ justifyContent: "flex-end" }}>
            <button className="popup-close" onClick={onClose}>X</button>
          </div>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔧</div>
          <h5 style={{ marginBottom: "12px", color: "#d9534f", fontWeight: "700" }}>
            Under Maintenance
          </h5>
          <p style={{ color: "#555", fontSize: "14px", lineHeight: "1.6", marginBottom: "24px" }}>
            Order purchases are temporarily paused for scheduled maintenance.<br />
            Please try again later. We apologize for the inconvenience.
          </p>
          <button className="cancel-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }
  // ── End maintenance gate ─────────────────────────────────────────────────────

  const handleConfirmClick = () => {
    if (!balanceLoaded) return;
    // Account balance must be more than 10000 + package amount to allow purchase
    const minRequiredBalance = 10000 + totalAmount;
    const balance = Number(availableBalance);
    if (balance <= minRequiredBalance) {
      Swal.fire({
        icon: "warning",
        title: "Insufficient Balance",
        text: "⚠️ Your available balance must be more than ₹" + minRequiredBalance.toFixed(2) + " (₹10,000 + package amount) to purchase. Please recharge your account.",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      return;
    }
    setShowTermsPopup(true);
  };

  const closeTermsPopup = () => {
    setShowTermsPopup(false); // Close Terms & Conditions popup
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          {/* Dynamically replace SMR BOTS with the selected plan name */}
          <h5 className="popup-title">
            {t("tax_invoice", { planName: selectedPlan.planName || t("service_plan") })}
          </h5>
          <button className="popup-close" onClick={onClose}>
            X
          </button>
        </div>
        <div className="popup-body">
          <div className="invoice-details">
            <div className="left-column">
              <div className="invoice-row">
                <span className="invoice-label">{t("service_name")}</span>
                <span className="invoice-value" style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: '200px'
                }} title={serviceName}>{serviceName}</span>

              </div>
              <div className="invoice-row">
                <span className="invoice-label">{t("purchase_date")}</span>
                <span className="invoice-value">{currentDate}</span>
              </div>
              <div className="invoice-row">
                <span className="invoice-label">{t("invoice_country")}</span>
                <span className="invoice-value">{t("country_name")}</span>
              </div>
            </div>
            <div className="right-column">
              <div className="invoice-row">
                <span className="invoice-label">{t("unit_price")}</span>
                <span className="invoice-value">₹{planCost}</span>
              </div>
              <div className="invoice-row">
                <span className="invoice-label">{t("gst_percentage")}</span>
                <span className="invoice-value">{gstPercentage}%</span>
              </div>
              <div className="invoice-row">
                <span className="invoice-label">{t("gst_amount")}</span>
                <span className="invoice-value">₹{gstAmount}</span>
              </div>

              <div className="invoice-row">
                <span className="invoice-label" style={{ fontWeight: "bold" }}>
                  {t("total_amount")}
                </span>
                <span className="invoice-value">
                  ₹{Number(totalAmount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="popup-footer">
          <button className="cancel-btn" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="confirm-btn"
            disabled={!balanceLoaded}
            onClick={(e) => {
              handleConfirmClick();
              e.target.blur(); // Removes focus immediately after click
            }}
            title={!balanceLoaded ? "Checking balance..." : ""}
          >
            {balanceLoaded ? t("confirm") : "Checking balance..."}
          </button>

        </div>
      </div>
      {showTermsPopup && (
        <TermsConditionsPopup
          onClose={onClose}
          selectedPlan={selectedPlan}
          planCost={planCost}
          gstAmount={gstAmount}
          gstPercentage={gstPercentage}
          totalAmount={totalAmount}
          serviceId={serviceId}
          serviceName={serviceName}
          autoDebitStatus={autoDebitStatus}
        />
      )}
    </div>
  );
};

export default PurchasePopup;
